import { FormEvent, useCallback, useEffect, useId, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import type { Strings } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { AdminFrame } from './AdminFrame';
import { errorText, rpc, type AppError } from './api';

// Phase 5 of the topic & follow-up check-in revision (spec §12.C, migration
// 0029): Zencera's own screens to manage the six wellbeing areas, the
// student-facing tiles, the answer-scale templates and the platform's own
// follow-up question bank. 0024/0025 already shipped every write RPC these
// screens call; 0029 added the one missing read, platform_list_questions.
// Four tabs, same shape as the school admin's own question bank
// (school_admin/QuestionsPage.tsx) and the school page's tab pattern
// (SchoolPage.tsx): a list + a fold-in create form + Dialog-confirmed
// retire, once per resource kind. Platform rows need no approval step
// (Zencera is both author and approver here), so there is no draft/
// pending/submit lifecycle, only active/retired.

type Status = 'active' | 'retired';
type Area = { code: string; status: Status; sort_order: number; created_at: string; retired_at: string | null };
type TileArea = { area_code: string; is_primary: boolean };
type Tile = { code: string; status: Status; sort_order: number; areas: TileArea[] };
type ScaleOption = { sort_order: number; emoji: string; label: string };
type Scale = { code: string; status: Status; options: ScaleOption[] };
type PlatformQuestion = {
  id: string;
  category_code: string;
  lens: 'think' | 'act' | 'reflect';
  axis: 'thriving' | 'strain';
  scale_template_code: string;
  direction: 'ascending' | 'descending';
  severity_weight: 0 | 1 | 2;
  question_text: string;
  status: 'approved' | 'retired';
  created_at: string;
};

const TABS = ['areas', 'tiles', 'scales', 'questions'] as const;
export type ContentTab = (typeof TABS)[number];

export function ContentPage() {
  const { t } = useI18n();
  const { tab = 'areas' } = useParams();
  const active = (TABS as readonly string[]).includes(tab) ? (tab as ContentTab) : 'areas';
  return (
    <AdminFrame section="content" title={t['r7.cg.title']}>
      {() => <Body active={active} />}
    </AdminFrame>
  );
}

function Body({ active }: { active: ContentTab }) {
  const { t } = useI18n();
  const tabLabel: Record<ContentTab, string> = {
    areas: t['r7.cg.tabAreas'],
    tiles: t['r7.cg.tabTiles'],
    scales: t['r7.cg.tabScales'],
    questions: t['r7.cg.tabQuestions'],
  };
  return (
    <>
      <p className="notice notice-block">{t['r7.cg.hint']}</p>
      <nav className="tabs" aria-label={t['r7.cg.title']}>
        {TABS.map((k) => (
          <Link
            key={k}
            to={`/admin/content${k === 'areas' ? '' : `/${k}`}`}
            className={`tabx${k === active ? ' on' : ''}`}
            aria-current={k === active ? 'page' : undefined}
          >
            {tabLabel[k]}
          </Link>
        ))}
      </nav>
      {active === 'areas' && <AreasTab />}
      {active === 'tiles' && <TilesTab />}
      {active === 'scales' && <ScalesTab />}
      {active === 'questions' && <QuestionsTab />}
    </>
  );
}

// ── Areas ────────────────────────────────────────────────────────────────

function AreasTab() {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [areas, setAreas] = useState<Area[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [code, setCode] = useState('');
  const [sort, setSort] = useState('');
  const [busy, setBusy] = useState(false);
  const [retire, setRetire] = useState<Area | null>(null);
  const [retireBusy, setRetireBusy] = useState(false);
  const [retireErr, setRetireErr] = useState<AppError | null>(null);

  const load = useCallback(async () => {
    const r = await rpc<Area[]>('platform_list_categories');
    if (r.error) setErr(r.error);
    else setAreas(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const r = await rpc('platform_create_category', { p_code: code.trim(), p_sort_order: sort ? Number(sort) : null });
    setBusy(false);
    if (r.error) return setErr(r.error);
    announce(t['r7.cg.areaAdded']);
    setCode('');
    setSort('');
    load();
  }

  async function runRetire() {
    if (!retire) return;
    setRetireBusy(true);
    setRetireErr(null);
    const r = await rpc('platform_retire_category', { p_code: retire.code });
    setRetireBusy(false);
    if (r.error) return setRetireErr(r.error);
    announce(t['r7.cg.areaRetiredToast']);
    setRetire(null);
    load();
  }

  if (err && !areas) return <p className="error-text" role="alert">{errorText(t, err)}</p>;
  if (!areas) return <p role="status">{t.loading}</p>;

  return (
    <>
      <section className="panel p5" aria-labelledby={`${id}-new`}>
        <h2 className="panel-title" id={`${id}-new`}>{t['r7.cg.areaNewTitle']}</h2>
        <p className="form-hint">{t['r7.cg.areaHint']}</p>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <form className="form" onSubmit={add} noValidate>
          <div className="form-row">
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-code`}>{t['r7.cg.areaCode']}</label>
              <input id={`${id}-code`} className="field" value={code} onChange={(e) => setCode(e.target.value)} aria-describedby={`${id}-code-hint`} />
              <p className="form-hint" id={`${id}-code-hint`}>{t['r7.cg.areaCodeHint']}</p>
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-sort`}>{t['r7.cg.areaSort']}</label>
              <input id={`${id}-sort`} className="field" type="number" value={sort} onChange={(e) => setSort(e.target.value)} aria-describedby={`${id}-sort-hint`} />
              <p className="form-hint" id={`${id}-sort-hint`}>{t['r7.cg.areaSortHint']}</p>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || code.trim().length < 1}>
              {t['r7.cg.areaAdd']}
            </button>
          </div>
        </form>
      </section>
      <section className="panel p5" aria-labelledby={`${id}-list`}>
        <h2 className="panel-title" id={`${id}-list`}>{t['r7.cg.areaListTitle']}</h2>
        {areas.length === 0 && <p className="text-muted">{t['r7.cg.areaEmpty']}</p>}
        <ul className="approval-list">
          {areas.map((a) => (
            <li key={a.code} className="approval">
              <div className="panel-head">
                <h3 className="panel-title">
                  {areaLabel(t, a.code)} <span className="text-muted">({a.code})</span>
                </h3>
                <Pill kind={a.status === 'active' ? 'good' : 'neutral'}>
                  {a.status === 'active' ? t['r7.cg.statusActive'] : t['r7.cg.statusRetired']}
                </Pill>
              </div>
              {a.status === 'active' && (
                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setRetire(a)}>
                    {t['r7.cg.areaRetire']}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
      <Dialog
        open={!!retire}
        title={t['r7.cg.areaRetireTitle']}
        intro={<p>{t['r7.cg.areaRetireBody']}</p>}
        submitLabel={t['r7.cg.areaRetire']}
        danger
        busy={retireBusy}
        error={retireErr ? errorText(t, retireErr) : null}
        onClose={() => {
          setRetire(null);
          setRetireErr(null);
        }}
        onSubmit={runRetire}
      />
    </>
  );
}

// ── Tiles ────────────────────────────────────────────────────────────────

function TilesTab() {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [tiles, setTiles] = useState<Tile[] | null>(null);
  const [areas, setAreas] = useState<Area[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [code, setCode] = useState('');
  const [sort, setSort] = useState('');
  const [busy, setBusy] = useState(false);
  const [retire, setRetire] = useState<Tile | null>(null);
  const [retireBusy, setRetireBusy] = useState(false);
  const [retireErr, setRetireErr] = useState<AppError | null>(null);
  // Per-tile "link an area" mini-form state, keyed by tile code.
  const [linkArea, setLinkArea] = useState<Record<string, string>>({});
  const [linkPrimary, setLinkPrimary] = useState<Record<string, boolean>>({});
  const [linkBusy, setLinkBusy] = useState<string | null>(null);
  const [linkErr, setLinkErr] = useState<AppError | null>(null);

  const load = useCallback(async () => {
    const [tl, ar] = await Promise.all([rpc<Tile[]>('platform_list_tiles'), rpc<Area[]>('platform_list_categories')]);
    if (tl.error) setErr(tl.error);
    else setTiles(tl.data);
    if (!ar.error) setAreas(ar.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const activeAreas = areas?.filter((a) => a.status === 'active') ?? [];

  async function add(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const r = await rpc('platform_create_tile', { p_code: code.trim(), p_sort_order: sort ? Number(sort) : null });
    setBusy(false);
    if (r.error) return setErr(r.error);
    announce(t['r7.cg.tileAdded']);
    setCode('');
    setSort('');
    load();
  }

  async function runRetire() {
    if (!retire) return;
    setRetireBusy(true);
    setRetireErr(null);
    const r = await rpc('platform_retire_tile', { p_code: retire.code });
    setRetireBusy(false);
    if (r.error) return setRetireErr(r.error);
    announce(t['r7.cg.tileRetiredToast']);
    setRetire(null);
    load();
  }

  async function link(tileCode: string) {
    const area = linkArea[tileCode];
    if (!area) return;
    setLinkBusy(tileCode);
    setLinkErr(null);
    const r = await rpc('platform_link_tile_area', { p_tile_code: tileCode, p_area_code: area, p_is_primary: !!linkPrimary[tileCode] });
    setLinkBusy(null);
    if (r.error) return setLinkErr(r.error);
    announce(t['r7.cg.tileLinked']);
    setLinkArea((m) => ({ ...m, [tileCode]: '' }));
    setLinkPrimary((m) => ({ ...m, [tileCode]: false }));
    load();
  }

  async function unlink(tileCode: string, areaCode: string) {
    setLinkBusy(tileCode);
    setLinkErr(null);
    const r = await rpc('platform_unlink_tile_area', { p_tile_code: tileCode, p_area_code: areaCode });
    setLinkBusy(null);
    if (r.error) return setLinkErr(r.error);
    announce(t['r7.cg.tileUnlinked']);
    load();
  }

  if (err && !tiles) return <p className="error-text" role="alert">{errorText(t, err)}</p>;
  if (!tiles || !areas) return <p role="status">{t.loading}</p>;

  return (
    <>
      <section className="panel p5" aria-labelledby={`${id}-new`}>
        <h2 className="panel-title" id={`${id}-new`}>{t['r7.cg.tileNewTitle']}</h2>
        <p className="form-hint">{t['r7.cg.tileHint']}</p>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <form className="form" onSubmit={add} noValidate>
          <div className="form-row">
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-code`}>{t['r7.cg.tileCode']}</label>
              <input id={`${id}-code`} className="field" value={code} onChange={(e) => setCode(e.target.value)} aria-describedby={`${id}-code-hint`} />
              <p className="form-hint" id={`${id}-code-hint`}>{t['r7.cg.tileCodeHint']}</p>
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-sort`}>{t['r7.cg.tileSort']}</label>
              <input id={`${id}-sort`} className="field" type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || code.trim().length < 1}>
              {t['r7.cg.tileAdd']}
            </button>
          </div>
        </form>
      </section>
      <section className="panel p5" aria-labelledby={`${id}-list`}>
        <h2 className="panel-title" id={`${id}-list`}>{t['r7.cg.tileListTitle']}</h2>
        {linkErr && <p className="error-text" role="alert">{errorText(t, linkErr)}</p>}
        {tiles.length === 0 && <p className="text-muted">{t['r7.cg.tileEmpty']}</p>}
        <ul className="approval-list">
          {tiles.map((tl) => {
            const linkedCodes = tl.areas.map((a) => a.area_code);
            const linkable = activeAreas.filter((a) => !linkedCodes.includes(a.code));
            return (
              <li key={tl.code} className="approval">
                <div className="panel-head">
                  <h3 className="panel-title">{tl.code}</h3>
                  <Pill kind={tl.status === 'active' ? 'good' : 'neutral'}>
                    {tl.status === 'active' ? t['r7.cg.statusActive'] : t['r7.cg.statusRetired']}
                  </Pill>
                </div>
                <p className="text-muted">
                  {t['r7.cg.tileAreasLabel']}:{' '}
                  {tl.areas.map((a) => (
                    <span key={a.area_code} className="chip">
                      {areaLabel(t, a.area_code)}
                      {a.is_primary ? ` · ${t['r7.cg.tilePrimaryBadge']}` : ''}
                      {tl.status === 'active' && linkedCodes.length > 1 && (
                        <button
                          type="button"
                          className="chip-x"
                          onClick={() => unlink(tl.code, a.area_code)}
                          disabled={linkBusy === tl.code}
                          aria-label={t['r7.cg.tileUnlinkAria'](areaLabel(t, a.area_code))}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                </p>
                {tl.status === 'active' && linkable.length > 0 && (
                  <div className="form-row">
                    <div className="field-group">
                      <label className="field-label" htmlFor={`${id}-area-${tl.code}`}>{t['r7.cg.tileAreaPick']}</label>
                      <div className="select-wrap">
                        <select
                          id={`${id}-area-${tl.code}`}
                          className="select"
                          value={linkArea[tl.code] ?? ''}
                          onChange={(e) => setLinkArea((m) => ({ ...m, [tl.code]: e.target.value }))}
                        >
                          <option value="">{t['r7.cg.tileAreaPick']}</option>
                          {linkable.map((a) => (
                            <option key={a.code} value={a.code}>{areaLabel(t, a.code)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={!!linkPrimary[tl.code]}
                        onChange={(e) => setLinkPrimary((m) => ({ ...m, [tl.code]: e.target.checked }))}
                      />
                      <span>{t['r7.cg.tilePrimaryCheck']}</span>
                    </label>
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={!linkArea[tl.code] || linkBusy === tl.code}
                        onClick={() => link(tl.code)}
                      >
                        {t['r7.cg.tileLink']}
                      </button>
                    </div>
                  </div>
                )}
                {tl.status === 'active' && (
                  <div className="form-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => setRetire(tl)}>
                      {t['r7.cg.tileRetire']}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>
      <Dialog
        open={!!retire}
        title={t['r7.cg.tileRetireTitle']}
        intro={<p>{t['r7.cg.tileRetireBody']}</p>}
        submitLabel={t['r7.cg.tileRetire']}
        danger
        busy={retireBusy}
        error={retireErr ? errorText(t, retireErr) : null}
        onClose={() => {
          setRetire(null);
          setRetireErr(null);
        }}
        onSubmit={runRetire}
      />
    </>
  );
}

// ── Scales ───────────────────────────────────────────────────────────────

const emptyOptions = [{ emoji: '', label: '' }, { emoji: '', label: '' }, { emoji: '', label: '' }];

function ScalesTab() {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [scales, setScales] = useState<Scale[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [code, setCode] = useState('');
  const [options, setOptions] = useState(emptyOptions);
  const [busy, setBusy] = useState(false);
  const [retire, setRetire] = useState<Scale | null>(null);
  const [retireBusy, setRetireBusy] = useState(false);
  const [retireErr, setRetireErr] = useState<AppError | null>(null);

  const load = useCallback(async () => {
    const r = await rpc<Scale[]>('platform_list_scale_templates');
    if (r.error) setErr(r.error);
    else setScales(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  function setOption(i: number, field: 'emoji' | 'label', value: string) {
    setOptions((os) => os.map((o, n) => (n === i ? { ...o, [field]: value } : o)));
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const r = await rpc('platform_create_scale_template', { p_code: code.trim(), p_options: options });
    setBusy(false);
    if (r.error) return setErr(r.error);
    announce(t['r7.cg.scaleAdded']);
    setCode('');
    setOptions(emptyOptions);
    load();
  }

  async function runRetire() {
    if (!retire) return;
    setRetireBusy(true);
    setRetireErr(null);
    const r = await rpc('platform_retire_scale_template', { p_code: retire.code });
    setRetireBusy(false);
    if (r.error) return setRetireErr(r.error);
    announce(t['r7.cg.scaleRetiredToast']);
    setRetire(null);
    load();
  }

  const optionLabels = [t['r7.cg.scaleOptionLow'], t['r7.cg.scaleOptionMid'], t['r7.cg.scaleOptionHigh']];
  const canAdd = code.trim().length > 0 && options.every((o) => o.emoji.trim() && o.label.trim());

  if (err && !scales) return <p className="error-text" role="alert">{errorText(t, err)}</p>;
  if (!scales) return <p role="status">{t.loading}</p>;

  return (
    <>
      <section className="panel p5" aria-labelledby={`${id}-new`}>
        <h2 className="panel-title" id={`${id}-new`}>{t['r7.cg.scaleNewTitle']}</h2>
        <p className="form-hint">{t['r7.cg.scaleHint']}</p>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <form className="form" onSubmit={add} noValidate>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-code`}>{t['r7.cg.scaleCode']}</label>
            <input id={`${id}-code`} className="field" value={code} onChange={(e) => setCode(e.target.value)} aria-describedby={`${id}-code-hint`} />
            <p className="form-hint" id={`${id}-code-hint`}>{t['r7.cg.scaleCodeHint']}</p>
          </div>
          {options.map((o, i) => (
            <div className="form-row" key={i}>
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-emoji-${i}`}>{optionLabels[i]} — {t['r7.cg.scaleEmoji']}</label>
                <input id={`${id}-emoji-${i}`} className="field" style={{ maxWidth: '5em' }} value={o.emoji} onChange={(e) => setOption(i, 'emoji', e.target.value)} />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-label-${i}`}>{optionLabels[i]} — {t['r7.cg.scaleOptionLabel']}</label>
                <input id={`${id}-label-${i}`} className="field" value={o.label} onChange={(e) => setOption(i, 'label', e.target.value)} />
              </div>
            </div>
          ))}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || !canAdd}>
              {t['r7.cg.scaleAdd']}
            </button>
          </div>
        </form>
      </section>
      <section className="panel p5" aria-labelledby={`${id}-list`}>
        <h2 className="panel-title" id={`${id}-list`}>{t['r7.cg.scaleListTitle']}</h2>
        {scales.length === 0 && <p className="text-muted">{t['r7.cg.scaleEmpty']}</p>}
        <ul className="approval-list">
          {scales.map((s) => (
            <li key={s.code} className="approval">
              <div className="panel-head">
                <h3 className="panel-title">
                  {scaleLabel(t, s.code)} <span className="text-muted">({s.code})</span>
                </h3>
                <Pill kind={s.status === 'active' ? 'good' : 'neutral'}>
                  {s.status === 'active' ? t['r7.cg.statusActive'] : t['r7.cg.statusRetired']}
                </Pill>
              </div>
              <p className="text-muted">{s.options.map((o) => `${o.emoji} ${o.label}`).join(' · ')}</p>
              {s.status === 'active' && (
                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setRetire(s)}>
                    {t['r7.cg.scaleRetire']}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
      <Dialog
        open={!!retire}
        title={t['r7.cg.scaleRetireTitle']}
        intro={<p>{t['r7.cg.scaleRetireBody']}</p>}
        submitLabel={t['r7.cg.scaleRetire']}
        danger
        busy={retireBusy}
        error={retireErr ? errorText(t, retireErr) : null}
        onClose={() => {
          setRetire(null);
          setRetireErr(null);
        }}
        onSubmit={runRetire}
      />
    </>
  );
}

// ── Platform questions ──────────────────────────────────────────────────

const LENSES = ['think', 'act', 'reflect'] as const;
const AXES = ['thriving', 'strain'] as const;
const SEVERITY = [0, 1, 2] as const;

const emptyQForm = {
  category: '',
  lens: 'act' as PlatformQuestion['lens'],
  axis: 'thriving' as PlatformQuestion['axis'],
  scale: '',
  direction: 'ascending' as 'ascending' | 'descending',
  text: '',
  weight: 0 as 0 | 1 | 2,
};

function QuestionsTab() {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [areas, setAreas] = useState<Area[] | null>(null);
  const [scales, setScales] = useState<Scale[] | null>(null);
  const [filter, setFilter] = useState('');
  const [questions, setQuestions] = useState<PlatformQuestion[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [form, setForm] = useState(emptyQForm);
  const [busy, setBusy] = useState(false);
  const [retire, setRetire] = useState<PlatformQuestion | null>(null);
  const [retireBusy, setRetireBusy] = useState(false);
  const [retireErr, setRetireErr] = useState<AppError | null>(null);

  const loadPickers = useCallback(async () => {
    const [a, s] = await Promise.all([rpc<Area[]>('platform_list_categories'), rpc<Scale[]>('platform_list_scale_templates')]);
    if (!a.error) setAreas(a.data);
    if (!s.error) setScales(s.data);
  }, []);
  useEffect(() => {
    loadPickers();
  }, [loadPickers]);

  const loadQuestions = useCallback(async () => {
    const r = await rpc<PlatformQuestion[]>('platform_list_questions', { p_category_code: filter || null });
    if (r.error) setErr(r.error);
    else setQuestions(r.data);
  }, [filter]);
  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      category: f.category || areas?.find((a) => a.status === 'active')?.code || '',
      scale: f.scale || scales?.find((s) => s.status === 'active')?.code || '',
    }));
  }, [areas, scales]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const r = await rpc('platform_create_question', {
      p_category_code: form.category,
      p_lens: form.lens,
      p_axis: form.axis,
      p_scale_template_code: form.scale,
      p_question_text: form.text,
      p_direction: form.direction,
      p_severity_weight: form.weight,
    });
    setBusy(false);
    if (r.error) return setErr(r.error);
    announce(t['r7.cg.qAdded']);
    setForm((f) => ({ ...emptyQForm, category: f.category, scale: f.scale }));
    loadQuestions();
  }

  async function runRetire() {
    if (!retire) return;
    setRetireBusy(true);
    setRetireErr(null);
    const r = await rpc('platform_retire_question', { p_question_id: retire.id });
    setRetireBusy(false);
    if (r.error) return setRetireErr(r.error);
    announce(t['r5.sq.retiredToast']);
    setRetire(null);
    loadQuestions();
  }

  const activeAreas = areas?.filter((a) => a.status === 'active') ?? [];
  const activeScales = scales?.filter((s) => s.status === 'active') ?? [];
  const scale = scales?.find((s) => s.code === form.scale);

  if (err && !questions) return <p className="error-text" role="alert">{errorText(t, err)}</p>;
  if (!areas || !scales || !questions) return <p role="status">{t.loading}</p>;

  return (
    <>
      <section className="panel p5" aria-labelledby={`${id}-new`}>
        <h2 className="panel-title" id={`${id}-new`}>{t['r7.cg.qNewTitle']}</h2>
        <p className="form-hint">{t['r7.cg.qHint']}</p>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <form className="form" onSubmit={add} noValidate>
          <div className="form-row">
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-area`}>{t['r5.sq.area']}</label>
              <div className="select-wrap">
                <select id={`${id}-area`} className="select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {activeAreas.map((a) => (
                    <option key={a.code} value={a.code}>{areaLabel(t, a.code)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-scale`}>{t['r5.sq.scale']}</label>
              <div className="select-wrap">
                <select id={`${id}-scale`} className="select" value={form.scale} onChange={(e) => setForm((f) => ({ ...f, scale: e.target.value }))}>
                  {activeScales.map((s) => (
                    <option key={s.code} value={s.code}>{scaleLabel(t, s.code)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {scale && (
            <p className="form-hint">
              {t['r5.sq.scalePreview']} {scale.options.map((o) => `${o.emoji} ${o.label}`).join(' · ')}
            </p>
          )}
          <div className="form-row">
            <fieldset className="field-group">
              <legend className="field-label">{t['r7.cg.qLens']}</legend>
              {LENSES.map((l) => (
                <label className="check" key={l}>
                  <input type="radio" name={`${id}-lens`} checked={form.lens === l} onChange={() => setForm((f) => ({ ...f, lens: l }))} />
                  <span>{t[`r7.cg.qLens${cap(l)}` as 'r7.cg.qLensAct']}</span>
                </label>
              ))}
            </fieldset>
            <fieldset className="field-group">
              <legend className="field-label">{t['r7.cg.qAxis']}</legend>
              {AXES.map((a) => (
                <label className="check" key={a}>
                  <input type="radio" name={`${id}-axis`} checked={form.axis === a} onChange={() => setForm((f) => ({ ...f, axis: a }))} />
                  <span>{t[`r7.cg.qAxis${cap(a)}` as 'r7.cg.qAxisThriving']}</span>
                </label>
              ))}
            </fieldset>
          </div>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-text`}>{t['r5.sq.text']}</label>
            <textarea
              id={`${id}-text`}
              className="field"
              rows={2}
              maxLength={300}
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              aria-describedby={`${id}-text-hint`}
            />
            <p className="form-hint" id={`${id}-text-hint`}>{t['r5.sq.textHint']}</p>
          </div>
          <div className="form-row">
            <fieldset className="field-group">
              <legend className="field-label">{t['r5.sq.direction']}</legend>
              <label className="check">
                <input type="radio" name={`${id}-dir`} checked={form.direction === 'descending'} onChange={() => setForm((f) => ({ ...f, direction: 'descending' }))} />
                <span>{t['r5.sq.directionDescending']}</span>
              </label>
              <label className="check">
                <input type="radio" name={`${id}-dir`} checked={form.direction === 'ascending'} onChange={() => setForm((f) => ({ ...f, direction: 'ascending' }))} />
                <span>{t['r5.sq.directionAscending']}</span>
              </label>
            </fieldset>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-weight`}>{t['r5.sq.weight']}</label>
              <div className="select-wrap">
                <select
                  id={`${id}-weight`}
                  className="select"
                  value={form.weight}
                  onChange={(e) => setForm((f) => ({ ...f, weight: Number(e.target.value) as 0 | 1 | 2 }))}
                  aria-describedby={`${id}-weight-hint`}
                >
                  {SEVERITY.map((w) => (
                    <option key={w} value={w}>{t[`r5.sq.weight${w}` as 'r5.sq.weight0']}</option>
                  ))}
                </select>
              </div>
              <p className="form-hint" id={`${id}-weight-hint`}>{t['r5.sq.weightHint']}</p>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || form.text.trim().length < 1}>
              {t['r7.cg.qAdd']}
            </button>
          </div>
        </form>
      </section>
      <section className="panel p5" aria-labelledby={`${id}-list`}>
        <div className="panel-head wrap">
          <h2 className="panel-title" id={`${id}-list`}>{t['r7.cg.qListTitle']}</h2>
          <span className="select-wrap">
            <select className="select" aria-label={t['r7.cg.qFilterLabel']} value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">{t['r7.cg.qFilterAll']}</option>
              {activeAreas.map((a) => (
                <option key={a.code} value={a.code}>{areaLabel(t, a.code)}</option>
              ))}
            </select>
          </span>
        </div>
        {questions.length === 0 && <p className="text-muted">{t['r7.cg.qEmpty']}</p>}
        <ul className="approval-list">
          {questions.map((q) => (
            <li key={q.id} className="approval">
              <p>
                <strong>{areaLabel(t, q.category_code)}</strong> · {t[`r7.cg.qLens${cap(q.lens)}` as 'r7.cg.qLensAct']} ·{' '}
                {t[`r7.cg.qAxis${cap(q.axis)}` as 'r7.cg.qAxisThriving']} · {scaleLabel(t, q.scale_template_code)}{' '}
                {q.status === 'approved' ? <Pill kind="good">{t['r5.sq.statusApproved']}</Pill> : <Pill kind="neutral">{t['r5.sq.statusRetired']}</Pill>}
              </p>
              <p>{q.question_text}</p>
              {q.status === 'approved' && (
                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setRetire(q)}>
                    {t['r5.sq.retire']}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
      <Dialog
        open={!!retire}
        title={t['r5.sq.retireTitle']}
        intro={<p>{t['r5.sq.retireBody']}</p>}
        submitLabel={t['r5.sq.retire']}
        danger
        busy={retireBusy}
        error={retireErr ? errorText(t, retireErr) : null}
        onClose={() => {
          setRetire(null);
          setRetireErr(null);
        }}
        onSubmit={runRetire}
      />
    </>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function areaLabel(t: Strings, code: string): string {
  return t[`area.${code}` as 'area.feel'] ?? code;
}
function scaleLabel(t: Strings, code: string): string {
  return t[`scale.${code}` as 'scale.frequency'] ?? code;
}
